import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore';
import { rskService } from './rskService';
import { progressService } from './progressService';
import { calendarSchedulingService } from './calendarSchedulingService';

/**
 * Daily Decision Service
 * Manages daily decision trees with course-specific questions
 */
export const dailyDecisionService = {
  /**
   * Check if user has completed today's decision
   */
  async hasCompletedToday(userId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const completionRef = doc(db, 'daily_decision_completions', `${userId}_${today}`);
      const completionDoc = await getDoc(completionRef);
      
      return completionDoc.exists();
    } catch (error) {
      console.error('Error checking daily completion:', error);
      return false;
    }
  },

  /**
   * Get today's question for the user
   */
  async getTodaysQuestion(userId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Check if user already has a question assigned for today
      const userQuestionRef = doc(db, 'user_daily_questions', `${userId}_${today}`);
      const userQuestionDoc = await getDoc(userQuestionRef);
      
      if (userQuestionDoc.exists()) {
        const data = userQuestionDoc.data();
        return { question: data.question, error: null };
      }
      
      // Generate a new question for today
      const question = await this.generateQuestion(userId);
      
      // Save the question for this user/date
      await setDoc(userQuestionRef, {
        userId,
        date: today,
        question,
        createdAt: new Date(),
      });
      
      return { question, error: null };
    } catch (error) {
      console.error('Error getting today\'s question:', error);
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        return { 
          question: null, 
          error: 'Database permissions not configured for daily decisions. Please contact administrator.' 
        };
      }
      
      return { question: null, error: error.message };
    }
  },

  /**
   * Generate a new question based on user's completed modules
   */
  async generateQuestion(userId) {
    try {
      // Get user's progress to find completed modules
      const { courses } = await progressService.getAllUserProgress(userId);
      const completedModules = [];
      
      // Extract all completed modules across all courses
      for (const courseProgress of courses) {
        if (courseProgress.modules) {
          Object.entries(courseProgress.modules).forEach(([moduleId, moduleData]) => {
            if (moduleData.completed) {
              completedModules.push({
                courseId: courseProgress.courseId,
                moduleId: moduleId,
                completedAt: moduleData.completedAt
              });
            }
          });
        }
      }
      
      console.log('[DailyDecision] Found completed modules:', completedModules.length);
      
      // Generate questions based on completed modules
      let moduleSpecificQuestions = [];
      for (const module of completedModules) {
        const moduleQuestions = this.getModuleSpecificQuestions(module.courseId, module.moduleId);
        moduleSpecificQuestions = [...moduleSpecificQuestions, ...moduleQuestions];
      }
      
      // If no module-specific questions, fall back to general questions
      const questionPool = moduleSpecificQuestions.length > 0 ? moduleSpecificQuestions : this.getGeneralBusinessQuestions();
      
      // Select a random question from the pool
      const selectedQuestion = questionPool[Math.floor(Math.random() * questionPool.length)];
      console.log('[DailyDecision] Selected question:', selectedQuestion.id);
      
      return selectedQuestion;
    } catch (error) {
      console.error('Error generating module-specific question:', error);
      // Fallback to general questions
      return this.getGeneralBusinessQuestions()[0];
    }
  },
  
  /**
   * Get questions specific to completed modules
   */
  getModuleSpecificQuestions(courseId, moduleId) {
    // Define questions based on specific modules the user has completed
    const moduleQuestions = {
      // Format: 'courseId_moduleId': [questions]
      'course1_module1': [
        {
          id: 'course1_m1_q1',
          title: 'Market Research Application',
          text: 'You just completed the market research fundamentals. A potential customer says "I love your idea!" during an interview. What should you do next?',
          answers: [
            {
              id: 'a1',
              text: 'Ask specific follow-up questions about their current solution and pain points',
              quality: 'best',
              rskReward: 100,
            },
            {
              id: 'a2',
              text: 'Ask if they would buy it right now',
              quality: 'better',
              rskReward: 50,
            },
            {
              id: 'a3',
              text: 'Thank them and move on to the next question',
              quality: 'good',
              rskReward: 10,
            },
            {
              id: 'a4',
              text: 'Assume they\'re a confirmed customer',
              quality: 'bad',
              rskReward: -10,
            },
            {
              id: 'a5',
              text: 'Start explaining all your product features',
              quality: 'worse',
              rskReward: -50,
            },
            {
              id: 'a6',
              text: 'Ask them to invest in your company',
              quality: 'worst',
              rskReward: -100,
            },
          ],
        },
      ],
      'course1_module2': [
        {
          id: 'course1_m2_q1',
          title: 'Customer Discovery Deep Dive',
          text: 'After completing the customer discovery module, you\'re interviewing potential customers. They describe a problem, but it\'s different from what you expected. What\'s your best response?',
          answers: [
            {
              id: 'a1',
              text: 'Listen carefully and ask follow-up questions to understand this new problem deeply',
              quality: 'best',
              rskReward: 100,
            },
            {
              id: 'a2',
              text: 'Take notes but try to steer the conversation back to your original problem',
              quality: 'better',
              rskReward: 50,
            },
            {
              id: 'a3',
              text: 'Acknowledge it but focus on validating your original hypothesis',
              quality: 'good',
              rskReward: 10,
            },
            {
              id: 'a4',
              text: 'Politely redirect them to discuss your solution',
              quality: 'bad',
              rskReward: -10,
            },
            {
              id: 'a5',
              text: 'Tell them that\'s not the problem you\'re solving',
              quality: 'worse',
              rskReward: -50,
            },
            {
              id: 'a6',
              text: 'Dismiss their problem as not important',
              quality: 'worst',
              rskReward: -100,
            },
          ],
        },
      ],
      // Add more module-specific questions here
    };

    const moduleKey = `${courseId}_${moduleId}`;
    return moduleQuestions[moduleKey] || [];
  },
  
  /**
   * Get course-specific questions (fallback for when no module-specific questions exist)
   */
  getCourseSpecificQuestions(courseId, courseProgress) {
    // Define course-level questions as fallback
    const courseQuestions = {
      'course1': [
        {
          id: 'course1_general_q1',
          title: 'Business Planning Fundamentals',
          text: 'Based on what you\'ve learned in this course, what\'s the most important factor in early-stage business success?',
          answers: [
            {
              id: 'a1',
              text: 'Understanding your customers\' real problems and needs',
              quality: 'best',
              rskReward: 100,
            },
            {
              id: 'a2',
              text: 'Having a great product or service',
              quality: 'better',
              rskReward: 50,
            },
            {
              id: 'a3',
              text: 'Getting funding quickly',
              quality: 'bad',
              rskReward: -10,
            },
            {
              id: 'a4',
              text: 'Having the lowest prices',
              quality: 'worse',
              rskReward: -50,
            },
            {
              id: 'a5',
              text: 'Being first to market',
              quality: 'good',
              rskReward: 10,
            },
            {
              id: 'a6',
              text: 'Having the most features',
              quality: 'worst',
              rskReward: -100,
            },
          ],
        },
      ],
    };

    return courseQuestions[courseId] || [];
  },
  
  /**
   * Get general business questions as fallback
   */
  getGeneralBusinessQuestions() {
    return [
      {
        id: 'general_q1',
        title: 'Market Research Approach',
        text: 'You\'re starting a new business and need to understand your target market. What\'s your first step?',
        answers: [
          {
            id: 'a1',
            text: 'Conduct comprehensive customer interviews and analyze competitor strategies thoroughly',
            quality: 'best',
            rskReward: 100,
          },
          {
            id: 'a2',
            text: 'Survey potential customers online and research the market size',
            quality: 'better',
            rskReward: 50,
          },
          {
            id: 'a3', 
            text: 'Create a basic business plan first, then worry about market research later',
            quality: 'good',
            rskReward: 10,
          },
          {
            id: 'a4',
            text: 'Launch a simple version and see what happens',
            quality: 'bad',
            rskReward: -10,
          },
          {
            id: 'a5',
            text: 'Copy what successful competitors are doing',
            quality: 'worse',
            rskReward: -50,
          },
          {
            id: 'a6',
            text: 'Skip research and trust your instincts completely',
            quality: 'worst',
            rskReward: -100,
          },
        ],
      },
      {
        id: 'general_q2',
        title: 'Financial Planning Priority',
        text: 'When starting your business, which financial aspect should you focus on first?',
        answers: [
          {
            id: 'a1',
            text: 'Create detailed cash flow projections and emergency fund planning',
            quality: 'best',
            rskReward: 100,
          },
          {
            id: 'a2',
            text: 'Focus on getting the first sale before worrying about detailed finances',
            quality: 'better',
            rskReward: 50,
          },
          {
            id: 'a3',
            text: 'Start spending on marketing and equipment immediately',
            quality: 'worst',
            rskReward: -100,
          },
        ],
      },
      {
        id: 'general_q3',
        title: 'Risk Management Strategy', 
        text: 'How should an entrepreneur approach risk when building their business?',
        answers: [
          {
            id: 'a1',
            text: 'Take calculated risks after thorough analysis and have contingency plans',
            quality: 'best',
            rskReward: 100,
          },
          {
            id: 'a2',
            text: 'Avoid all risks and play it completely safe',
            quality: 'worse',
            rskReward: -50,
          },
          {
            id: 'a3',
            text: 'Take big risks quickly - fortune favors the bold',
            quality: 'bad',
            rskReward: -10,
          },
        ],
      },
      {
        id: 'general_q4',
        title: 'Time Management for Entrepreneurs',
        text: 'What\'s the most effective way to manage your time as a new entrepreneur?',
        answers: [
          {
            id: 'a1',
            text: 'Focus on high-impact activities that directly drive revenue and customer acquisition',
            quality: 'best',
            rskReward: 100,
          },
          {
            id: 'a2',
            text: 'Work on everything equally to make sure nothing gets missed',
            quality: 'bad',
            rskReward: -10,
          },
          {
            id: 'a3',
            text: 'Spend most time perfecting the product before doing anything else',
            quality: 'worse',
            rskReward: -50,
          },
        ],
      },
      {
        id: 'general_q5',
        title: 'Funding Strategy Decision',
        text: 'An investor offers you funding but wants 60% equity. How do you respond?',
        answers: [
          {
            id: 'a1',
            text: 'Evaluate the offer carefully - consider the money, mentorship, and long-term implications',
            quality: 'best',
            rskReward: 100,
          },
          {
            id: 'a2',
            text: 'Accept immediately - any funding is good funding',
            quality: 'worse',
            rskReward: -50,
          },
          {
            id: 'a3',
            text: 'Reject it outright - you don\'t want to give up control',
            quality: 'bad',
            rskReward: -10,
          },
        ],
      },
    ];
  },

  /**
   * Submit user's answer to today's question
   */
  async submitAnswer(userId, questionId, answerId, answerQuality, rskReward) {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Ensure answerQuality is not undefined
      if (!answerQuality) {
        throw new Error('answerQuality is required but was undefined');
      }
      
      // Record the completion
      const completionRef = doc(db, 'daily_decision_completions', `${userId}_${today}`);
      await setDoc(completionRef, {
        userId,
        date: today,
        questionId,
        answerId,
        answerQuality,
        rskReward,
        completedAt: new Date(),
      });

      // Award RSK points
      if (rskReward > 0) {
        await rskService.awardRSK(userId, rskReward, `Daily Decision: ${answerQuality} answer`);
      } else if (rskReward < 0) {
        await rskService.deductRSK(userId, Math.abs(rskReward), `Daily Decision: ${answerQuality} answer`);
      }

      // Mark calendar event as completed
      await calendarSchedulingService.markScheduledEventComplete(userId, 'dailyDecision', new Date());

      return { success: true, error: null };
    } catch (error) {
      console.error('Error submitting daily decision:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get today's completed answer for review
   */
  async getTodaysAnswer(userId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const completionRef = doc(db, 'daily_decision_completions', `${userId}_${today}`);
      const completionDoc = await getDoc(completionRef);
      
      if (completionDoc.exists()) {
        const completion = completionDoc.data();
        
        // Get the question details
        const { question } = await this.getTodaysQuestion(userId);
        
        return {
          question,
          selectedAnswerId: completion.answerId,
          answerQuality: completion.answerQuality,
          rskReward: completion.rskReward,
          completedAt: completion.completedAt
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting today\'s answer:', error);
      return null;
    }
  },

  /**
   * Get user's decision history
   */
  async getUserDecisionHistory(userId, limitCount = 30) {
    try {
      const decisionsQuery = query(
        collection(db, 'daily_decision_completions'),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc'),
        limit(limitCount)
      );
      
      const decisionsSnapshot = await getDocs(decisionsQuery);
      const decisions = decisionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      return { decisions, error: null };
    } catch (error) {
      console.error('Error getting decision history:', error);
      return { decisions: [], error: error.message };
    }
  },

  /**
   * Get user's current streak
   */
  async getUserStreak(userId) {
    try {
      const { decisions } = await this.getUserDecisionHistory(userId, 365); // Check last year
      
      let currentStreak = 0;
      const today = new Date();
      
      // Sort decisions by date (newest first)
      decisions.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      // Calculate streak
      for (let i = 0; i < decisions.length; i++) {
        const decisionDate = new Date(decisions[i].date);
        const daysDiff = Math.floor((today - decisionDate) / (1000 * 60 * 60 * 24));
        
        if (daysDiff === i) {
          currentStreak++;
        } else {
          break;
        }
      }
      
      return { streak: currentStreak, error: null };
    } catch (error) {
      console.error('Error calculating streak:', error);
      return { streak: 0, error: error.message };
    }
  },
};